// src/lib/safeHtml.js — server-rendered contract HTML is cleaned before it touches the page
// (security review 2026-09-17).
//
// The contract, the signature block and the handover annex arrive as HTML strings and were
// injected as-is. They carry what people TYPED — party names, addresses, free-text clauses — and
// the signing page is PUBLIC. The server is expected to escape those values; this is the second
// lock, so that one missed escape on the server is a wrong-looking name and not a script running
// in a client's browser on the page where they sign.
//
// ⚠️ DISPLAY ONLY. The signed bytes and their sha256 live on the server and are never derived
// from the DOM, so cleaning what is drawn cannot change what was signed. DOMPurify's defaults
// keep everything a contract uses (tables, headings, lang/class/style attributes, data: images
// for signatures) and drop what it never needs: <script>, event handlers, javascript: URLs,
// <iframe>/<object>/<embed>, <form> controls posting elsewhere.
import DOMPurify from 'dompurify'

const CONFIG = { USE_PROFILES: { html: true }, ADD_ATTR: ['target'], FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'style'], FORBID_ATTR: ['action', 'formaction'] }

// `target="_blank"` is kept (a contract links to its terms in a new tab) but never without
// `noopener`: the opened page must not get a handle on the signing page.
let hooked = false
function hook() {
  if (hooked || !DOMPurify.addHook) return
  hooked = true
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A' && node.getAttribute('target')) {
      const rel = new Set((node.getAttribute('rel') || '').split(/\s+/).filter(Boolean)); rel.add('noopener')
      node.setAttribute('rel', [...rel].join(' '))
    }
  })
}

/** Clean an HTML string for dangerouslySetInnerHTML. Non-strings and empty input give ''. */
export function safeHtml(html) {
  if (typeof html !== 'string' || !html) return ''
  hook()
  return DOMPurify.sanitize(html, CONFIG)
}
